import React, { useEffect, useMemo, useState } from 'react';
import { ProviderComponentProps } from '../../types/api';
import { apiService } from '../../services/api';

interface DatacapPayload {
  scriptAddress: string;
  tokenKey: string;
}

interface DatacapTokenResponse {
  Token?: string;
  Brand?: string;
  ExpirationMonth?: string;
  ExpirationYear?: string;
  Last4?: string;
  Bin?: string;
  CVV?: string;
  Error?: string;
}

declare global {
  interface Window {
    DatacapHostedWebToken?: {
      init: (tokenKey: string, containerId: string, callback: (response: DatacapTokenResponse) => void, customCss?: string) => void;
      requestToken: () => void;
    };
  }
}

const IFRAME_CONTAINER_ID = 'datacap-token-iframe';

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`) as HTMLScriptElement | null;
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve();
      } else if (existing.dataset.loaded === 'error') {
        reject(new Error(`Failed to load script: ${src}`));
      } else {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error(`Failed to load script: ${src}`)), { once: true });
      }
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => { script.dataset.loaded = 'true'; resolve(); };
    script.onerror = () => { script.dataset.loaded = 'error'; reject(new Error(`Failed to load script: ${src}`)); };
    document.head.appendChild(script);
  });
}

const DatacapProvider: React.FC<ProviderComponentProps> = ({ sid, payloadBase64 }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [scriptReady, setScriptReady] = useState(false);
  const [error, setError] = useState('');
  const [callbackError, setCallbackError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const payload = useMemo<DatacapPayload | null>(() => {
    try {
      const decoded = JSON.parse(atob(payloadBase64));
      return {
        scriptAddress: decoded.ScriptAddress || decoded.scriptAddress,
        tokenKey: decoded.TokenKey || decoded.tokenKey,
      };
    } catch {
      console.error('Failed to decode Datacap payload');
      return null;
    }
  }, [payloadBase64]);

  // Step 1: Load the SDK script
  useEffect(() => {
    if (!payload) return;
    let cancelled = false;

    loadScript(payload.scriptAddress)
      .then(() => { if (!cancelled) setScriptReady(true); })
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load payment form.');
          setIsLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [payload]);

  // Step 2: Initialize SDK after script is loaded, with a delay so the
  // iframe DOM element survives React StrictMode's remount cycle.
  useEffect(() => {
    if (!scriptReady || !payload) return;

    const timer = setTimeout(() => {
      const iframe = document.getElementById(IFRAME_CONTAINER_ID);
      if (!iframe || !window.DatacapHostedWebToken) return;

      const callback = (response: DatacapTokenResponse) => {
        const callbackPayload = {
          token: response.Token ?? null,
          brand: response.Brand ?? null,
          expirationMonth: response.ExpirationMonth ?? null,
          expirationYear: response.ExpirationYear ?? null,
          last4: response.Last4 ?? null,
          bin: response.Bin ?? null,
          cvv: response.CVV ?? null,
          error: response.Error ?? null,
        };

        if (response.Error) {
          setCallbackError(response.Error);
          setIsSubmitting(false);
          return;
        }

        apiService.hppDatacapCallback({ sid, payload: callbackPayload }).catch((err) => {
          console.error('Failed to send Datacap callback:', err);
        });
      };

      window.DatacapHostedWebToken.init(payload.tokenKey, IFRAME_CONTAINER_ID, callback);
      setIsLoading(false);
    }, 100);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptReady, payload, sid]);

  const handleSubmit = () => {
    if (window.DatacapHostedWebToken) {
      setCallbackError('');
      setIsSubmitting(true);
      window.DatacapHostedWebToken.requestToken();
    }
  };

  if (error) {
    return (
      <div className="text-red-600 text-center py-4">{error}</div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-btn" />
          <span className="ml-3 text-text-disabled">Loading payment form...</span>
        </div>
      )}

      <iframe
        id={IFRAME_CONTAINER_ID}
        title="Datacap Payment Form"
        className={`w-full border-0 transition-all ${isLoading ? 'h-0 overflow-hidden opacity-0' : 'h-[260px] opacity-100'}`}
        frameBorder={0}
      />

      {!isLoading && callbackError && (
        <p className="text-red-600 text-sm text-center">{callbackError}</p>
      )}

      {!isLoading && (
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={`btn-primary w-full flex items-center justify-center gap-2 ${isSubmitting ? '!cursor-wait' : ''}`}
        >
          {isSubmitting && (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
          )}
          {isSubmitting ? 'Processing...' : 'Continue'}
        </button>
      )}
    </div>
  );
};

export default DatacapProvider;
